import * as React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { Card } from '../../components/Card/Card'
import { Layout } from '../../components/Layout/Layout'
import Loader from '../../components/Loader/Loader'
import { Navbar } from '../../components/Navbar/Navbar'
import { CompanyId, Role, UserCompanyDto, UserProfileDto } from '../../services/api.types'
import { getFullName } from '../helpers/translations'
import { MAPPED_ROUTES, ROUTES } from './routePaths'
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop'
import GAListener from './GAListener'
import YumChallengeTabsWrapper from '../../components/YumChallengeTabs/YumChallengeTabsWrapper'
import YumChallengeTitle from '../../components/YumChallengeTitle/YumChallengeTitleWrapper'
import YumChallengeTitleWrapper from '../../components/YumChallengeTitle/YumChallengeTitleWrapper'

export interface IStateProps {
  hasAnyRole(roles: Role[]): boolean
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  authenticated: boolean
  userDto: UserProfileDto
  currentRoute: Partial<Location>
  userCompanies: UserCompanyDto[]
}

export interface IDispatchProps {
  doLogOut(): void
  getWhoAmI(): void
}

type IRoutesProps = IStateProps & IDispatchProps

export class Routes extends React.Component<IRoutesProps> {
  defaultUserDto = {
    //companyName: '',
    email: '',
    firstName: '',
    lastName: '',
    //role: Role.ANONYMOUS,
  }

  componentDidMount() {
    // console.log('Routes | componentDidMount -> getWhoAmI')
    this.props.getWhoAmI()
  }

  Loading = () => (
    <Card className="pt-12">
      <Loader />
    </Card>
  )

  Fallback = () => <Redirect to={ROUTES.LANDING.to} />

  Routes = () =>
    MAPPED_ROUTES.map(({ to, component, roles }, index) => {
      const { hasAnyRole } = this.props
      return hasAnyRole(roles) && <Route exact={true} key={`route-${index}`} path={to} component={component} />
    })

  isStatisticsHidden = () => {
    return (
      this.props.userCompanies &&
      !this.props.userCompanies.some((uc) => {
        const showFunFacts = !uc.hideStatisticsView
        const showAdminReport = uc.reportRequired && this.props.hasRoleInCompany(uc.companyId, Role.ADMIN)
        const showUserReport = uc.reportRequired && uc.reportRequiredForUsers
        return showFunFacts || showAdminReport || showUserReport
      })
    )
  }

  isRankingHidden = () => this.props.userCompanies && !this.props.userCompanies.some((uc) => !uc.hideRankingView)

  isProgressHidden = () => !this.props.hasAnyRole([Role.USER])

  Header = () => {
    const { hasAnyRole, authenticated, userDto, currentRoute } = this.props
    const { firstName, lastName, email, onboardingStatus, serviceProvider } = userDto || this.defaultUserDto

    const currentRouteIsPublic: boolean = MAPPED_ROUTES.some((route: any) => {
      return route.isPublic && route.to === currentRoute.pathname
    })
    const isLanguageSelectionVisible = !authenticated && currentRouteIsPublic
    const isAdmin = hasAnyRole([Role.ADMIN])
    const navRoutes: any = MAPPED_ROUTES.map((i) => {
      const { roles, inNavigation, to } = i

      if (to === '/statistics' && this.isStatisticsHidden()) {
        return null
      }

      if (to === '/ranking' && this.isRankingHidden()) {
        return null
      }

      if (to === '/progress' && this.isProgressHidden()) {
        return null
      }

      //const hasNavRoutes = hasAnyRole([Role.ADMIN, Role.USER]) && currentRoute.pathname !== ROUTES.REGISTER_USER.to
      const hasNavRoutes = hasAnyRole([Role.AUTHENTICATED]) && currentRoute.pathname !== ROUTES.REGISTER_USER.to
      const noSettingsForAdmin = !(isAdmin && to === ROUTES.SETTINGS.to)
      if (hasNavRoutes && hasAnyRole(roles) && inNavigation && noSettingsForAdmin && !currentRouteIsPublic) {
        return i
      }

      return null
    }).filter(Boolean)

    const brandCompany = this.props.userCompanies ? this.props.userCompanies.find((uc) => uc.brandLogoUrl) : false
    const brandLogoUrl = brandCompany ? brandCompany.brandLogoUrl : ''

    const newUserAdmin =
      this.props.userCompanies && this.props.userCompanies.some((uc) => uc.scope?.includes('newUserAdmin'))

    return (
      <Navbar
        routes={navRoutes}
        authenticated={authenticated}
        fullName={getFullName(firstName, lastName, email)}
        isLanguageSelectionVisible={isLanguageSelectionVisible}
        currentRoute={currentRoute}
        isAdmin={isAdmin}
        onboardingStatus={onboardingStatus}
        brandLogoUrl={brandLogoUrl}
        serviceProvider={serviceProvider}
        newUserAdmin={newUserAdmin}
      />
    )
  }

  render() {
    const { currentRoute } = this.props
    const { pathname } = currentRoute

    const isNarrow = pathname === ROUTES.SETTINGS.to

    const pathParts = currentRoute.pathname ? currentRoute.pathname.split('/') : []
    const isChallengePage = !!(pathParts[1] === 'challenges' && pathParts[3] && pathParts[3] != 'edit')
    const goalId = isChallengePage ? pathParts[2] : undefined

    // Because we're not using hashHistory for Cordova anymore.
    const Router = 'div'
    //const Router = isNative() ? HashRouter : 'div'
    const content = (
      <GAListener trackingId="UA-144824224-1">
        <Router className={'router'}>
          <ScrollToTop>
            <React.Suspense fallback={this.Loading()}>
              <Switch>
                {this.Routes()}
                <Route component={this.Fallback} />
              </Switch>
            </React.Suspense>
          </ScrollToTop>
        </Router>
      </GAListener>
    )

    // Don't display landing page in Cordova, tied to routePaths.ts
    return (
      <Layout header={this.Header()} isNarrow={isNarrow}>
        {isChallengePage && (
          <>
            <div className="show-on-desktop" style={{ paddingBottom: 20 }}>
              <YumChallengeTitleWrapper goalId={goalId} noMargin={true} />
            </div>
            <YumChallengeTabsWrapper goalId={goalId} tab={pathParts[3]} />
          </>
        )}
        {content}
      </Layout>
    )
  }
}
